import React from 'react';
import { X } from 'lucide-react';

/**
 * Modal base reutilizable para todos los modales admin.
 * Soporta dos layouts: centrado simple y responsive (fullscreen móvil).
 *
 * @param {Function} onClose - Handler para cerrar el modal
 * @param {string} title - Título del header
 * @param {string} [subtitle] - Subtítulo opcional
 * @param {ReactNode} [icon] - Icono del header (e.g. <Archive className="w-6 h-6 text-amber-400" />)
 * @param {string} [iconBg='bg-amber-500/20'] - Clase Tailwind para el fondo del icono
 * @param {string} [maxWidth='max-w-md'] - Clase de ancho máximo (usar 'lg:max-w-2xl' para responsive)
 * @param {string} [borderColor='border-slate-700'] - Clase de color del borde
 * @param {boolean} [responsive=false] - Si true, usa fullscreen en móvil/tablet
 * @param {ReactNode} [footer] - Contenido del footer (botones)
 * @param {ReactNode} children - Contenido del body
 */
const BaseModal = ({
  onClose,
  title,
  subtitle,
  icon,
  iconBg = 'bg-amber-500/20',
  maxWidth = 'max-w-md',
  borderColor = 'border-slate-700',
  responsive = false,
  footer,
  children
}) => {
  const header = (
    <div className={`${responsive ? 'p-4 lg:p-6' : 'p-6'} border-b border-slate-700 flex items-center justify-between ${responsive ? 'flex-shrink-0' : ''}`}>
      <div className="flex items-center gap-3">
        {icon && (
          <div className={`${iconBg} p-2 rounded-lg`}>
            {icon}
          </div>
        )}
        <div>
          <h2 className={`${responsive ? 'text-lg lg:text-xl' : 'text-xl'} font-bold text-white`}>
            {title}
          </h2>
          {subtitle && (
            <p className="text-slate-400 text-sm mt-1">{subtitle}</p>
          )}
        </div>
      </div>
      <button
        onClick={onClose}
        className="text-slate-400 hover:text-white transition-colors"
      >
        <X className="w-5 h-5" />
      </button>
    </div>
  );

  const bodyClasses = responsive
    ? 'p-4 lg:p-6 space-y-4 flex-1'
    : 'p-6 space-y-4';

  const footerClasses = responsive
    ? 'p-4 lg:p-6 border-t border-slate-700 flex gap-3 flex-shrink-0'
    : 'p-6 border-t border-slate-700 flex gap-3';

  if (responsive) {
    return (
      <div className="fixed inset-0 z-50 overflow-hidden">
        <div
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={onClose}
        />
        <div className="fixed inset-0 lg:flex lg:items-center lg:justify-center lg:p-4">
          <div
            className={`relative bg-slate-800 w-full h-full flex flex-col border-slate-700 lg:rounded-2xl lg:shadow-2xl ${maxWidth} lg:h-auto lg:border lg:max-h-[95vh] border-t ${borderColor} overflow-y-auto`}
            onClick={(e) => e.stopPropagation()}
          >
            {header}
            <div className={bodyClasses}>{children}</div>
            {footer && <div className={footerClasses}>{footer}</div>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <div
        className={`bg-slate-800 rounded-2xl ${maxWidth} w-full border ${borderColor} overflow-hidden`}
        onClick={(e) => e.stopPropagation()}
      >
        {header}
        <div className={bodyClasses}>{children}</div>
        {footer && <div className={footerClasses}>{footer}</div>}
      </div>
    </div>
  );
};

export default BaseModal;
